import { Checkbox, Dropdown, Input, Spin } from 'antd'
import { ItemType } from 'antd/lib/menu/hooks/useItems'
import Down from 'helper/Icons/DownIcon'
import { DownOutlined } from '@ant-design/icons'
import React, { useState, useRef, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getFilters } from 'api/Blogs/Blogs'
import Loader from 'helper/Loader'
import { useBlogPostsViewStore } from 'state/blog/blogPostsView'
import './Filter_module.scss'

export const BlogFilter = ({ setParms, parms, isLoading: parentIsLoading }: any) => {
  const dropdownRef = useRef<HTMLDivElement | null>(null)
  const [open, setOpen] = useState(0)
  const [selectedVisibility, setSelectedVisibility] = useState([])
  const [selectedAuthors, setSelectedAuthors] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [isApplyingFilter, setIsApplyingFilter] = useState(false)
  const { view, changeView } = useBlogPostsViewStore()
  const { data: blogFilter, isLoading, refetch } = useQuery({
    queryKey: ['blog-filter'],
    queryFn: () => getFilters(),
    enabled: false
  })

  useEffect(() => {
    refetch()
  }, [])

  // Reset applying filter state when parent loading is complete
  useEffect(() => {
    if (!parentIsLoading) {
      setIsApplyingFilter(false)
    }
  }, [parentIsLoading])


  const DEFAULT_VISIBILITY = ['public', 'draft']
  const visibilityOptions = [
    {
      label: 'Public',
      value: 'public',
    },
    {
      label: 'Draft',
      value: 'draft',
    },
    {
      label: 'Scheduled',
      value: 'scheduled',
    },
    {
      label: 'Archived',
      value: 'archived',
    },
  ]

  const authors = blogFilter?.authors || []
  const authorOptions = authors.map((author: any) => ({
    label: author.firstName + ' ' + author.lastName,
    value: author._id
  }))
  const filteredAuthorOptions = authorOptions?.filter((option: any) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  const clear = (item: string, setFn: (value: any) => void) => {
    if (item in parms) {
      const { [item]: _, ...updatedParms } = parms
      // If clearing visibility filter, restore default to exclude archived
      if (item === 'visibility') {
        updatedParms.visibility = DEFAULT_VISIBILITY
      }
      setParms(updatedParms)
    }
    setFn && setFn([])
  }

  const handleVisibilityChange = selectedValues => {
    setSelectedVisibility(selectedValues)
  }

  const handleAuthorChange = selectedValues => {
    setSelectedAuthors(selectedValues)
  }

  const tableViewOptions: ItemType[] = [
    {
      key: 'table',
      label: (
        <div onClick={() => {
          changeView('table')
          setOpen(0)
        }}>
          Table View
        </div>
      ),
    },
    {
      key: 'card',
      label: (
        <div onClick={() => {
          changeView('card')
          setOpen(0)
        }}>
          Card View
        </div>
      ),
    },
  ]

  const visibilityFilters: ItemType[] = [
    {
      key: 'visibility',
      label: (
        <>
          <Checkbox.Group
            options={visibilityOptions}
            className="category-checkbox"
            onChange={handleVisibilityChange}
            value={selectedVisibility}
          />
          <div className="flex justify-between mx-15px mt-10px">
            <button
              className="clr-btn"
              onClick={() => {
                clear('visibility', setSelectedVisibility)
              }}
            >
              Clear
            </button>
            <button
              onClick={() => {
                setIsApplyingFilter(true)
                setOpen(0)
                selectedVisibility &&
                  setParms(prevParms => ({
                    ...prevParms,
                    visibility: selectedVisibility.length > 0
                      ? selectedVisibility
                      : DEFAULT_VISIBILITY,
                    page: 1,
                  }))
              }}
              className="aply-btn"
              disabled={isApplyingFilter || parentIsLoading}
            >
              {(isApplyingFilter || parentIsLoading) ? <Loader /> : 'Apply'}
            </button>
          </div>
        </>
      ),
    },
  ]


  const authorFilters: ItemType[] = [
    {
      key: 'authors',
      label: (
        <div>
          <div className="filter-body">
            <Input
              placeholder="Search..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
            <Checkbox.Group
              options={filteredAuthorOptions}
              className="category-checkbox"
              value={selectedAuthors}
              onChange={handleAuthorChange}
            />
          </div>
          <div className="filter-footer">
            <button
              className="clr-btn"
              onClick={() => {
                clear('authorIds', setSelectedAuthors)
              }}
            >
              Clear
            </button>
            <button
              onClick={() => {
                setIsApplyingFilter(true)
                setOpen(0)
                selectedAuthors &&
                  setParms(prevParms => ({
                    ...prevParms,
                    authorIds: selectedAuthors,
                    page: 1,
                  }))
              }}
              className="aply-btn"
              disabled={isApplyingFilter || parentIsLoading}
            >
              Apply
            </button>
          </div>
        </div>
      ),
    },
  ]

  const handleDocumentClick = e => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
      setOpen(0)
    }
  }

  const handleDropdownClick = e => {
    e.stopPropagation()
  }

  return (
    <div className="flex justify-between align-center flex-wrap">
      <div
        className="flex align-center flex-wrap"
        ref={dropdownRef}
        onClick={handleDropdownClick}
      >
        <h2 className="w-500 text-14 mr-20px">Filter by:</h2>
        <Dropdown
          menu={{
            items: visibilityFilters,
          }}
          placement="bottomLeft"
          arrow={false}
          className="mr-10px"
          onOpenChange={(isOpen) => setOpen(isOpen ? 1 : 0)}
          open={open === 1}
          overlayStyle={{ width: '280px' }}
          overlayClassName="custom-dropdown-overlay"
        >
          <button
            className={` ${open === 1 ? 'filter-btn-click' : 'filter-btn'
              } w-500`}
            onClick={() => {
              setOpen(open === 1 ? 0 : 1)
            }}
          >
            Visibility <Down />
          </button>
        </Dropdown>
        <Dropdown
          menu={{
            items: authorFilters,
          }}
          placement="bottomLeft"
          arrow={false}
          className="mr-10px"
          onOpenChange={(isOpen) => setOpen(isOpen ? 2 : 0)}
          open={open === 2}
          overlayStyle={{ width: '280px' }}
          overlayClassName="custom-dropdown-overlay"
        >
          <button
            className={` ${open === 2 ? 'filter-btn-click' : 'filter-btn'
              } w-500`}
            onClick={() => {
              setOpen(open === 2 ? 0 : 2)
            }}
          >
            Author <Down />
          </button>
        </Dropdown>
      </div>
      <div className="flex align-center">
        <Dropdown
          menu={{
            items: tableViewOptions,
          }}
          placement="bottomLeft"
          arrow={false}
          onOpenChange={(isOpen) => setOpen(isOpen ? 3 : 0)}
          open={open === 3}
          overlayStyle={{ width: '150px' }}
          overlayClassName="custom-dropdown-overlay"
        >
          <button
            onClick={() => {
              setOpen(open === 3 ? 0 : 3)
            }}
            className="borderd-btn"
          >
            {view === 'card' ? 'Card view' : 'Table view'}
            <DownOutlined style={{ marginLeft: 6 }} />

          </button>
        </Dropdown>
      </div>
    </div>
  )
}
export default BlogFilter
